import { Children, cloneElement, ReactElement, ReactNode, FC } from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { createBemBlockBuilder } from "@ebsco-ui/utils";

import "./text-control.scss";

export interface TextControlProps {
  label: ReactNode;
  error?: string;
  description?: string;
  className?: string;
  children: ReactElement;
  labelButton?: ReactElement;
}

const TextControl: FC<TextControlProps> = ({
  label,
  error,
  description,
  className,
  children,
  labelButton,
}) => {
  const getBlocksWith = createBemBlockBuilder(["eb-text-control", className]);

  const child = Children.only(children);

  let inputId;
  if (child.props.id) {
    inputId = child.props.id;
  } else {
    console.error(
      "Please provide an id for your input. This is necessary to help describe the purpose of the control.",
    );
  }

  const childWithClass = cloneElement(child, {
    className: cn(child.props.className, getBlocksWith("__control")),
  });

  return (
    <div className={cn(getBlocksWith(), error && getBlocksWith("--invalid"))}>
      <div className={getBlocksWith("__label-wrapper")}>
        <label htmlFor={inputId} className={getBlocksWith("__label")}>
          {label}
        </label>
        {labelButton}
      </div>
      {description && (
        <div
          data-auto="text-control-description"
          className={getBlocksWith("__description")}
          id={cn(inputId && `${inputId}-description`)}
        >
          {description}
        </div>
      )}
      {childWithClass}
      <p
        className={getBlocksWith("__error")}
        id={cn(inputId && `${inputId}-error`)}
      >
        {error && (
          <>
            <FontAwesomeIcon
              className={getBlocksWith("__error-icon")}
              icon={faExclamationCircle}
              aria-hidden="true"
            />
            {error}
          </>
        )}
      </p>
    </div>
  );
};

export default TextControl;
